import { type ChangeEvent, type ReactElement, useState } from 'react';
import { Check, Copy, ImagePlus, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useApiRequest, useAuth, useWorkspace } from '~/app/providers';
import { AppImage } from '~/components/ui/AppImage';
import { Button } from '~/components/ui/button';
import { uploadPrivateAsset } from '~/repositories/assetRepository';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const PROVIDERS = ['google', 'vk'] as const;

export const AccountProfilePage = (): ReactElement => {
  const { t } = useTranslation('account');
  const request = useApiRequest();
  const { account, session, refresh } = useAuth();
  const { activeCompany } = useWorkspace();
  const user = account?.user;
  const [displayName, setDisplayName] = useState(
    user?.displayName ?? session?.user.displayName ?? ''
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const currentName = user?.displayName ?? session?.user.displayName ?? '';
  const avatarUrl = user?.avatarUrl ?? session?.user.avatarUrl;
  const initials = currentName.slice(0, 2).toUpperCase() || 'MY';

  const save = async (): Promise<void> => {
    const normalizedName = displayName.trim();
    if (!normalizedName) {
      setError(t('displayNameRequired'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (normalizedName !== currentName) {
        await request('/auth/me', {
          method: 'PATCH',
          body: JSON.stringify({ displayName: normalizedName }),
        });
      }
      await refresh();
      setDisplayName(normalizedName);
      setMessage(t('changesSaved'));
    } catch {
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const changeAvatar = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setMessage('');
    if (!AVATAR_TYPES.has(file.type)) {
      setError(t('avatarTypeError'));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('avatarSizeError'));
      return;
    }
    if (!activeCompany) {
      setError(t('avatarUploadFailed'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const asset = await uploadPrivateAsset(
        activeCompany.id,
        file,
        'IMAGE',
        'OTHER',
        {
          onHashProgress: value => setUploadProgress(value * 0.15),
          onUploadProgress: value => setUploadProgress(0.15 + value * 0.85),
        }
      );
      await request('/auth/me/avatar', {
        method: 'PATCH',
        body: JSON.stringify({ assetId: asset.id }),
      });
      await refresh();
      setUploadProgress(1);
      setMessage(t('avatarUpdated'));
    } catch {
      setError(t('avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const copyUserId = async (): Promise<void> => {
    if (!user?.id) return;
    await navigator.clipboard.writeText(user.id);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 pb-12 page-in">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          Mylo ID
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {t('accountProfile')}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted sm:text-base">
          {t('profileDescription')}
        </p>
      </header>

      <section className="overflow-hidden rounded-3xl border border-border bg-elevated shadow-sm">
        <div className="flex flex-col gap-5 border-b border-border-subtle bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:flex-row sm:items-center sm:p-8">
          <div className="rounded-full bg-primary/15 p-1.5 ring-1 ring-primary/40">
            <span className="grid size-24 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-primary via-violet-600 to-fuchsia-700 text-2xl font-semibold text-white">
              {avatarUrl ? (
                <AppImage
                  alt={currentName}
                  className="size-full object-cover"
                  height={96}
                  src={avatarUrl}
                  width={96}
                  wrapperClassName="size-full"
                />
              ) : (
                initials
              )}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-semibold text-foreground">
              {currentName}
            </h2>
            <p className="mt-1 truncate text-sm text-muted">
              {user?.email ?? session?.user.email ?? '—'}
            </p>
            <label className="mt-4 inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground transition hover:bg-hover focus-within:ring-2 focus-within:ring-primary/40">
              <ImagePlus aria-hidden className="size-4" />
              {uploading
                ? t('uploadingAvatar', {
                    progress: Math.round(uploadProgress * 100),
                  })
                : t('changeAvatar')}
              <input
                accept="image/jpeg,image/png,image/webp"
                aria-label={t('changeAvatar')}
                className="sr-only"
                disabled={uploading}
                onChange={event => void changeAvatar(event)}
                type="file"
              />
            </label>
            <p className="mt-2 text-xs text-subtle">{t('avatarHint')}</p>
          </div>
        </div>

        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(16rem,0.65fr)]">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('personalInfo')}
            </h2>
            <div className="mt-5 grid gap-5">
              <label className="grid gap-2 text-sm font-medium text-foreground">
                {t('displayName')}
                <input
                  autoComplete="name"
                  className="min-h-11 rounded-xl border border-border bg-background px-3.5 text-foreground outline-none transition placeholder:text-subtle focus:border-primary focus:ring-2 focus:ring-primary/20"
                  name="displayName"
                  onChange={event => setDisplayName(event.target.value)}
                  value={displayName}
                />
              </label>
              <ReadOnlyField label={t('email')}>
                {user?.email ?? session?.user.email ?? '—'}
              </ReadOnlyField>
              <ReadOnlyField label={t('userId')}>
                <span className="flex min-w-0 items-center justify-between gap-3">
                  <span className="truncate font-mono text-xs">
                    {user?.id ?? '—'}
                  </span>
                  <button
                    aria-label={copied ? t('copied') : t('copyUserId')}
                    className="grid size-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
                    disabled={!user?.id}
                    onClick={() => void copyUserId()}
                    type="button"
                  >
                    {copied ? (
                      <Check aria-hidden className="size-4" />
                    ) : (
                      <Copy aria-hidden className="size-4" />
                    )}
                  </button>
                </span>
              </ReadOnlyField>
              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
              {message ? (
                <p className="text-sm text-emerald-600" role="status">
                  {message}
                </p>
              ) : null}
              <Button
                className="min-h-11 justify-self-start rounded-xl px-5"
                disabled={saving || uploading}
                onClick={() => void save()}
                type="button"
              >
                {saving ? t('saving') : t('saveChanges')}
              </Button>
            </div>
          </div>

          <aside>
            <h2 className="text-lg font-semibold text-foreground">
              {t('signInMethods')}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted">
              {t('signInMethodsHint')}
            </p>
            <div className="mt-5 grid gap-3">
              {PROVIDERS.map(provider => {
                const connected = user?.providers.includes(provider) ?? false;
                return (
                  <div
                    className="flex items-center gap-3 rounded-2xl border border-border-subtle bg-background p-4"
                    key={provider}
                  >
                    <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary">
                      <Link2 aria-hidden className="size-4" />
                    </span>
                    <span className="flex-1 font-medium text-foreground">
                      {provider === 'google' ? 'Google' : 'VK'}
                    </span>
                    <span
                      className={
                        connected
                          ? 'text-xs font-medium text-emerald-600'
                          : 'text-xs text-subtle'
                      }
                    >
                      {connected ? t('connected') : t('notConnected')}
                    </span>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

const ReadOnlyField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement => (
  <div className="grid gap-2 text-sm">
    <span className="font-medium text-foreground">{label}</span>
    <div className="min-h-11 rounded-xl border border-border-subtle bg-surface px-3.5 py-3 text-muted">
      {children}
    </div>
  </div>
);
